import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { AdminArticleEditDialog } from "./AdminArticleEditDialog";

const STATUS_TABS = [
  { id: "pending_review", label: "Awaiting review" },
  { id: "draft", label: "Drafts" },
  { id: "published", label: "Published" },
];

const STATUS_PILL = {
  draft: "bg-[#F4EFE6] text-[#1E3A8A] border-[#1E3A8A]/30",
  pending_review: "bg-[#FFF8E1] text-[#A87900] border-[#F4C430]/60",
  published: "bg-[#E8F5E9] text-[#1B5E20] border-[#2E7D32]/40",
};

export default function AdminArticles() {
  const [tab, setTab] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/articles?status=${status}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/articles/${id}`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load article");
    }
  };

  const publish = async (id) => {
    try {
      await api.post(`/admin/articles/${id}/publish`);
      toast.success("Article published");
      setPreview(null);
      await load(tab);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    }
  };

  const unpublish = async (id) => {
    if (!window.confirm("Move this article back to drafts and remove it from the public site?")) return;
    try {
      await api.post(`/admin/articles/${id}/unpublish`);
      toast.success("Article unpublished");
      setPreview(null);
      await load(tab);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unpublish failed");
    }
  };

  return (
    <div data-testid="admin-articles" className="p-8 lg:p-12">
      <p className="label-eyebrow text-[#1E3A8A]">— Editorial</p>
      <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#111] mt-2">
        Article queue
      </h1>
      <p className="text-sm text-[#555] mt-2 max-w-2xl">
        Writers submit drafts here. Publish to push them to the public African Stories column.
      </p>

      <div className="mt-8 flex items-center gap-2 border-b border-[#E5E0D8]" data-testid="article-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`article-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`label-eyebrow px-4 py-3 border-b-2 ${
              tab === t.id ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#555] hover:text-[#111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="label-eyebrow text-[#555]">— Loading</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#555]">Nothing here.</p>
        ) : (
          items.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-article-row-${a.id}`}
              className="border border-[#E5E0D8] bg-white p-5 flex items-start justify-between gap-6"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`label-eyebrow px-2 py-0.5 border ${STATUS_PILL[a.status] || STATUS_PILL.draft}`}>
                    {a.status.replace("_", " ").toUpperCase()}
                  </span>
                  <h3 className="font-serif-display text-xl tracking-tight text-[#111]">{a.title}</h3>
                </div>
                <p className="text-xs text-[#555] mt-2">
                  By {a.author_name} · {new Date(a.updated_at).toLocaleString()}
                </p>
                {a.excerpt && <p className="text-sm text-[#333] mt-2">{a.excerpt}</p>}
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <button
                  onClick={() => openPreview(a.id)}
                  data-testid={`preview-article-${a.id}`}
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#111]"
                >
                  Preview
                </button>
                <button
                  onClick={() => setEditId(a.id)}
                  data-testid={`edit-article-${a.id}`}
                  className="label-eyebrow text-[#1E3A8A] hover:text-[#111]"
                  title={
                    a.status === "published"
                      ? "Amend this published article — changes go live immediately"
                      : "Edit article"
                  }
                >
                  Edit
                </button>
                {a.status !== "published" && (
                  <button
                    onClick={() => publish(a.id)}
                    data-testid={`publish-article-${a.id}`}
                    className="label-eyebrow text-[#1B5E20] hover:text-[#111]"
                  >
                    Publish
                  </button>
                )}
                {a.status === "published" && (
                  <button
                    onClick={() => unpublish(a.id)}
                    data-testid={`unpublish-article-${a.id}`}
                    className="label-eyebrow text-[#555] hover:text-[#D32F2F]"
                  >
                    Unpublish
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {preview && (
        <div
          data-testid="article-preview-modal"
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="label-eyebrow text-[#1E3A8A]">— Preview</p>
            <h2 className="font-serif-display text-3xl mt-2 text-[#111]">{preview.title}</h2>
            <p className="text-xs text-[#555] mt-1">By {preview.author_name}</p>
            {preview.cover_image && (
              <img src={preview.cover_image} alt="" className="mt-4 w-full" />
            )}
            {preview.excerpt && <p className="text-base text-[#333] mt-4 italic">{preview.excerpt}</p>}
            <div className="mt-4 whitespace-pre-wrap text-[#222] leading-relaxed">{preview.body}</div>
            <div className="mt-6 flex items-center gap-3 pt-4 border-t border-[#E5E0D8]">
              {preview.status !== "published" && (
                <button
                  onClick={() => publish(preview.id)}
                  data-testid="preview-publish-btn"
                  className="label-eyebrow bg-[#1E3A8A] text-white px-4 py-2"
                >
                  Publish now
                </button>
              )}
              {preview.status === "published" && (
                <button
                  onClick={() => unpublish(preview.id)}
                  data-testid="preview-unpublish-btn"
                  className="label-eyebrow border border-[#1E3A8A] text-[#1E3A8A] px-4 py-2"
                >
                  Unpublish
                </button>
              )}
              <button
                onClick={() => setPreview(null)}
                className="label-eyebrow text-[#555] hover:text-[#111]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminArticleEditDialog
        articleId={editId}
        open={!!editId}
        onClose={() => setEditId(null)}
        onSaved={() => load(tab)}
      />
    </div>
  );
}
